import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

export default function ContactPage() {
  const { settings } = useSettings();
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    address: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (response.ok) {
        setSubmitted(true);
        setForm({ name: '', company: '', email: '', phone: '', service: '', address: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
    setSubmitting(false);
  };

  return (
    <main className="pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom">
          <p className="section-label mb-4 text-[#F5F1EC]/60">Contact</p>
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl text-[#F5F1EC] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Tell us what you are <span className="italic text-[#B8A066]">building.</span>
          </h1>
          <p 
            className="text-lg text-[#F5F1EC]/60 max-w-2xl"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Every note is read personally. We respond within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 md:py-32 bg-[#F5F1EC]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Form */}
            <div>
              {submitted ? (
                <div className="text-center py-16">
                  <h3 
                    className="text-2xl text-[#3D3A38] mb-4"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    Thank you for your inquiry.
                  </h3>
                  <p 
                    className="text-[#5C5855] mb-8"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    We will get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="btn-secondary"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="form-label">Your Name</label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="form-input-underline"
                        placeholder="First and last"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="form-label">Company</label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        className="form-input-underline"
                        placeholder="Developer or owner"
                        data-testid="contact-company"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="form-input-underline"
                        placeholder="name@company.com"
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="form-input-underline"
                        placeholder="+1"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Service Type</label>
                    <select
                      value={form.service}
                      onChange={(e) => setForm({ ...form, service: e.target.value })}
                      className="form-input-underline cursor-pointer"
                      data-testid="contact-service"
                    >
                      <option value="">Select one</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Residential">Residential</option>
                      <option value="Industrial">Industrial</option>
                      <option value="Panel Upgrades">Panel Upgrades</option>
                      <option value="Lighting & Controls">Lighting & Controls</option>
                      <option value="EV Charging">EV Charging</option>
                      <option value="Not sure yet">Not sure yet</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Site Address (if applicable)</label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="form-input-underline"
                      placeholder="Project location"
                      data-testid="contact-address"
                    />
                  </div>

                  <div>
                    <label className="form-label">Brief</label>
                    <textarea
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="form-input-underline resize-none"
                      placeholder="Site, square footage, timeline, anything we should know."
                      data-testid="contact-message"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <p 
                      className="text-xs text-[#5C5855] italic"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      Every note is read personally.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid="contact-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Inquiry <ArrowRight size={14} />
                        </>
                      )}
                    </button>
                  </div>

                  <p 
                    className="text-sm text-[#B8A066] text-center mt-4"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    We respond back in 24 hours
                  </p>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:pl-12 lg:border-l border-[#E5DFD5]">
              <h3 
                className="text-2xl text-[#3D3A38] mb-12"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
              >
                Or, if you prefer the long way around.
              </h3>

              <div className="space-y-8">
                <div className="flex gap-8">
                  <p className="text-xs tracking-[0.15em] uppercase text-[#5C5855]/60 w-20" style={{ fontFamily: 'Outfit, sans-serif' }}>Office</p>
                  <p className="text-sm text-[#3D3A38]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {settings.address}
                  </p>
                </div>

                <div className="flex gap-8">
                  <p className="text-xs tracking-[0.15em] uppercase text-[#5C5855]/60 w-20" style={{ fontFamily: 'Outfit, sans-serif' }}>Hours</p>
                  <div className="text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <p className="text-[#3D3A38]">{settings.business_hours}</p>
                    <p className="text-[#5C5855] mt-1">{settings.emergency_hours}</p>
                  </div>
                </div>

                <div className="flex gap-8">
                  <p className="text-xs tracking-[0.15em] uppercase text-[#5C5855]/60 w-20" style={{ fontFamily: 'Outfit, sans-serif' }}>Direct</p>
                  <div className="text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <a href={`mailto:${settings.email}`} className="block text-[#3D3A38] hover:text-[#B8A066] transition-colors link-underline">
                      {settings.email}
                    </a>
                    <a href={`tel:${settings.phone}`} className="block text-[#3D3A38] hover:text-[#B8A066] transition-colors mt-1">
                      {settings.phone}
                    </a>
                  </div>
                </div>

                <div className="flex gap-8">
                  <p className="text-xs tracking-[0.15em] uppercase text-[#5C5855]/60 w-20" style={{ fontFamily: 'Outfit, sans-serif' }}>Compliance</p>
                  <div className="text-sm text-[#3D3A38]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <p>Licensed # 7018736</p>
                    <p className="text-[#5C5855] mt-1">ESA Certified</p>
                  </div>
                </div>
              </div>

              {/* Map placeholder or additional info */}
              <div className="mt-16 p-8 bg-[#3D3A38]">
                <p 
                  className="text-[#F5F1EC]/60 text-sm mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Serving the Greater Toronto Area
                </p>
                <p 
                  className="text-[#F5F1EC] text-lg"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  Toronto · Vaughan · Mississauga · Brampton · Etobicoke
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
